import { createElement } from 'react';
import { Routes, Route, useLocation, useNavigate, useParams } from 'react-router-dom';
import { type ContextualNavSection } from '@vnetwork/ui/layout';
import { useMyServices } from '@/api/services';
import { OpennebulaProvider, useZoneFeatures } from '@/api/opennebula';
import { useSetContextualNav } from '@/components/layout/nav-context';
import { categoryBackTarget } from '../categories';
import { COMPUTE_NAV_GROUPS, INSTANCE_NAV, computeHref, computeActiveId } from './nav';

// Compute has its own category landing — send "Back" there, not to all-services.
const COMPUTE_BACK = categoryBackTarget('compute');
import { InstanceList } from './instances/InstanceList';
import { InstanceDetail } from './instances/InstanceDetail';
import { InstanceCreate } from './instances/InstanceCreate';
import { ComputeDashboard } from './ComputeDashboard';
import { QuotasUsage } from './QuotasUsage';
import { BusinessUsage } from './BusinessUsage';
import { SshKeys } from './SshKeys';
import { SecurityGroups } from './SecurityGroups';
import { Networking } from './Networking';
import { RouterDetail } from './networking/RouterDetail';
import { Vpc } from './Vpc';
import { VpnElasticIp } from './VpnElasticIp';
import { LoadBalancers } from './LoadBalancers';
import { Certificates } from './Certificates';
import { Volumes } from './Volumes';
import { Snapshots } from './Snapshots';
import { Backups } from './Backups';
import { ComputeSettings } from './ComputeSettings';

/**
 * Service-scoped OpenNebula console. The per-service navigation lives in the app's
 * main `ContextualSidebar` (registered via `useSetContextualNav`) — entering a
 * compute service swaps the whole sidebar to its scope with a "Back to Services"
 * link, instead of nesting a secondary rail inside the content. Wrapped in
 * OpennebulaProvider so detail pages get adaptive polling.
 */
export function ComputeConsole() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { serviceId } = useParams<{ serviceId: string }>();
  // Loads the service list so the contextual sidebar can scope to this service.
  useMyServices();
  const base = `/services/compute/${serviceId}`;

  // Hide nav entries the service's zone doesn't offer (default: everything shown).
  const { isEnabled, zone } = useZoneFeatures(serviceId);

  useSetContextualNav(() => {
    // Instance scope — inside /instances/:instanceId/* the sidebar swaps to the
    // instance's sub-sections with a "Back to instances" link. Single registration
    // point on purpose: a child registration in InstanceDetail would be clobbered
    // by this console's effect re-running on every pathname change.
    const rel = pathname.slice(base.length).split('/').filter(Boolean);
    const instanceId = rel[0] === 'instances' && rel[1] && rel[1] !== 'create' ? rel[1] : null;
    if (instanceId) {
      const instanceBase = `${base}/instances/${instanceId}`;
      const instanceHref = (slug: string) => (slug ? `${instanceBase}/${slug}` : instanceBase);
      const sub = rel[2] ?? '';
      const known = INSTANCE_NAV.some((n) => n.slug === sub);
      return {
        sections: [
          {
            heading: 'Instance',
            items: INSTANCE_NAV.map((entry) => ({
              id: instanceHref(entry.slug),
              label: entry.label,
              icon: createElement(entry.icon, { size: 16 }),
              onSelect: () => navigate(instanceHref(entry.slug)),
            })),
          },
        ],
        // Unknown sub-paths (e.g. `resize`) render Overview — highlight it too.
        activeId: known && sub ? instanceHref(sub) : instanceBase,
        back: { label: 'Back to instances', onSelect: () => navigate(`${base}/instances`) },
      };
    }

    const sections: ContextualNavSection[] = COMPUTE_NAV_GROUPS.map((group) => ({
      heading: group.heading,
      items: group.items
        .filter((entry) => isEnabled(entry.feature))
        .map((entry) => {
          const href = computeHref(base, entry.slug);
          return {
            id: href,
            label: entry.label,
            icon: createElement(entry.icon, { size: 16 }),
            onSelect: () => navigate(href),
          };
        }),
    })).filter((section) => section.items.length > 0);
    return {
      sections,
      activeId: computeActiveId(base, pathname),
      back: { label: COMPUTE_BACK.label, onSelect: () => navigate(COMPUTE_BACK.path) },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base, pathname, navigate, zone]);

  // Every compute sub-page supplies its own page chrome (Container + PageHeader),
  // matching the @console / Kubernetes model — so the console renders the routes
  // bare, with no stacked service-level header.
  return (
    <OpennebulaProvider>
      <Routes>
      <Route index element={<ComputeDashboard />} />
      <Route path="instances" element={<InstanceList />} />
      <Route path="instances/create" element={<InstanceCreate />} />
      <Route path="instances/:instanceId/*" element={<InstanceDetail />} />
      <Route path="networks/routers/:routerId" element={<RouterDetail />} />
      <Route path="networks/*" element={<Networking />} />
      <Route path="vpc/*" element={<Vpc />} />
      <Route path="vpn/*" element={<VpnElasticIp />} />
      <Route path="load-balancers/*" element={<LoadBalancers />} />
      <Route path="certificates/*" element={<Certificates />} />
      <Route path="security-groups/*" element={<SecurityGroups />} />
      <Route path="ssh-keys/*" element={<SshKeys />} />
      <Route path="volumes/*" element={<Volumes />} />
      <Route path="snapshots/*" element={<Snapshots />} />
      <Route path="backups/*" element={<Backups />} />
      <Route path="quotas/*" element={<QuotasUsage />} />
      <Route path="business/*" element={<BusinessUsage />} />
      <Route path="settings" element={<ComputeSettings />} />
      <Route path="*" element={<ComputeDashboard />} />
      </Routes>
    </OpennebulaProvider>
  );
}
